import { supabase } from '@/integrations/supabase/client'
import { QueryClient } from '@tanstack/react-query'

interface SubscriptionConfig {
  table: string
  event?: string
  queryKeys: string[]
  callback?: (payload: any) => void
}

class CentralizedSubscriptionService {
  private static instance: CentralizedSubscriptionService
  private subscriptions: Map<string, any> = new Map()
  private subscribers: Map<string, Set<string>> = new Map()
  private queryClient: QueryClient | null = null

  private constructor() {}

  static getInstance(): CentralizedSubscriptionService {
    if (!CentralizedSubscriptionService.instance) {
      CentralizedSubscriptionService.instance = new CentralizedSubscriptionService()
    }
    return CentralizedSubscriptionService.instance
  }

  setQueryClient(queryClient: QueryClient) {
    this.queryClient = queryClient
  }

  subscribe(subscriptionKey: string, hookName: string, config: SubscriptionConfig) {
    // Subscribe hook to centralized channel
    
    // Add subscriber to this subscription
    if (!this.subscribers.has(subscriptionKey)) {
      this.subscribers.set(subscriptionKey, new Set())
    }
    this.subscribers.get(subscriptionKey)!.add(hookName)

    // Create subscription only if it doesn't exist and we have a query client
    if (!this.subscriptions.has(subscriptionKey) && this.queryClient) {
      const channelName = `${subscriptionKey}-${Date.now()}`
      
      const channel = supabase
        .channel(channelName)
        .on(
          'postgres_changes' as any,
          {
            event: config.event || '*',
            schema: 'public',
            table: config.table
          },
          (payload) => {
            // Call custom callback if provided (callbacks should handle refreshing)
            if (config.callback) {
              config.callback(payload)
            }
          }
        )
        .subscribe()
      
      this.subscriptions.set(subscriptionKey, channel)
    }
  }

  unsubscribe(subscriptionKey: string, hookName: string) {
    const subscribers = this.subscribers.get(subscriptionKey)
    if (subscribers) {
      subscribers.delete(hookName)

      // Remove subscription if no more subscribers
      if (subscribers.size === 0) {
        const channel = this.subscriptions.get(subscriptionKey)
        if (channel) {
          supabase.removeChannel(channel)
          this.subscriptions.delete(subscriptionKey)
        }
        this.subscribers.delete(subscriptionKey)
      }
    }
  }

  getSubscriberCount(subscriptionKey: string): number {
    return this.subscribers.get(subscriptionKey)?.size || 0
  }

  // Legacy methods for backward compatibility
  setQueryClient_legacy = this.setQueryClient
  subscribe_legacy(hookName: string) {
    this.subscribe('mailboxes', hookName, {
      table: 'mailboxes',
      queryKeys: ['mailboxes', 'staging-sync-status', 'smartlead-sync-status', 'mailbox-health']
    })
  }
  unsubscribe_legacy(hookName: string) {
    this.unsubscribe('mailboxes', hookName)
  }
}

export const centralizedSubscriptionService = CentralizedSubscriptionService.getInstance()

// Legacy export for backward compatibility
export const mailboxSubscriptionService = {
  setQueryClient: centralizedSubscriptionService.setQueryClient_legacy,
  subscribe: centralizedSubscriptionService.subscribe_legacy.bind(centralizedSubscriptionService),
  unsubscribe: centralizedSubscriptionService.unsubscribe_legacy.bind(centralizedSubscriptionService),
  getSubscriberCount: () => centralizedSubscriptionService.getSubscriberCount('mailboxes')
}